export type TokenClaims = {
    role?: string;
    full_name?: string;
    email?: string;
    exp?: number;
    [key: string]: unknown;
};

// Converts a base64-encoded text chunk into plain text using whatever decoder the browser or Node provides.
const decodeBase64 = (value: string): string => {
    if (typeof globalThis.atob === "function") {
        return globalThis.atob(value);
    }
    const maybeBuffer = (globalThis as { Buffer?: { from: (input: string, encoding: string) => { toString: (enc: string) => string } } }).Buffer;
    if (maybeBuffer) {
        return maybeBuffer.from(value, "base64").toString("binary");
    }
    throw new Error("Base64 decoding is not supported in this environment");
};

// Pulls the middle section of a JWT and turns it into a friendly object with the claim fields we care about.
export const decodeTokenClaims = (token?: string | null): TokenClaims => {
    if (!token) {
        return {};
    }
    try {
        const [, payload] = token.split(".");
        if (!payload) {
            return {};
        }
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const decoded = decodeBase64(normalized);
        return JSON.parse(decoded);
    } catch {
        return {};
    }
};

// Chooses the first page a user should land on after login based on their role, falling back to the dashboard.
/** Where a signed-in user lands. EVERY role starts on the dashboard (owner's
 *  decision, 14-Sep-2026): super admins used to land on Users and coaching
 *  admins on the AI Workbench. `role` is kept so a role can differ again later. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getDefaultRouteForRole = (_role?: string | null): string => "/dashboard";

// Checks whether the JWT has passed its "exp" timestamp so we can log the user out right away.
export const isTokenExpired = (token?: string | null): boolean => {
    if (!token) {
        return true;
    }
    const claims = decodeTokenClaims(token);
    const exp = typeof claims.exp === "number" ? claims.exp : Number(claims.exp);
    if (!exp || Number.isNaN(exp)) {
        return false;
    }
    const expiresAtMs = exp * 1000;
    return Date.now() >= expiresAtMs;
};
